import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, AuthenticationErrorEnum } from 'shared/contexts/AuthContext';
import { useMutation } from './useMutation';
import { useSitemap } from './useSitemap';

interface OTPResponse {
  ok: boolean;
}

export const useAuthentication = () => {
  const { isLogoutAllowed, setIsAuthenticated, checkAuthenticationStatus } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const sitemap = useSitemap();

  const { setAuthenticationError } = useContext(AuthContext);

  const [requestSendOTP, { loading: sendOTPLoading }] = useMutation<OTPResponse>();
  const [requestVerifyOTP, { loading: verifyOTPLoading }] = useMutation<
    { otp: string },
    OTPResponse
  >();
  const [requestLogout, { loading: logoutLoading }] = useMutation<OTPResponse>();

  const sendOneTimePassword = useCallback(async () => {
    await requestSendOTP('send-otp');
  }, []);

  const verifyOneTimePassword = useCallback(
    async (otp: string) => {
      setAuthenticationError(null);
      const res = await requestVerifyOTP('verify-otp', { otp });
      if (!res?.ok) {
        setAuthenticationError(AuthenticationErrorEnum.incorrectOTP);
      } else {
        await checkAuthenticationStatus();
        navigate(sitemap.dashboard);
      }
    },
    [sitemap.dashboard],
  );

  const logout = useCallback(async () => {
    setAuthenticationError(null);
    if (!isLogoutAllowed) {
      setAuthenticationError(AuthenticationErrorEnum.logoutFailed);
      return;
    }

    const res = await requestLogout('logout');
    if (!res?.ok) {
      setAuthenticationError(AuthenticationErrorEnum.logoutFailed);
    }
    setIsAuthenticated(false);
    navigate(sitemap.dashboard);
  }, [sitemap.dashboard, isLogoutAllowed]);

  return {
    sendOneTimePassword,
    sendOTPLoading,
    verifyOneTimePassword,
    verifyOTPLoading,
    logout,
    logoutLoading,
  };
};
